import { isNullDataPoint } from '@customComp/Chart/chartConfigs/nullHandling/isNullDataPoint';
import { ChartType, type TChartType } from '@dbl/core/chart';

/** 列占比虚拟列前缀，与 column_sort 一致：`% 指标名`。 */
const PERCENTAGE_PREFIX = '% ';
/** 后端 table 列名内嵌占比前缀，如 `t0_% meta_score`。 */
const EMBEDDED_PERCENT_COLUMN_PATTERN = /^t\d+_%/;

/** 下载用表格列元信息，对应计算接口 `column_name` 单项。 */
export interface IPercentAxisChartDownloadColumn {
  name: string;
  type?: number;
  column_date_type?: string;
}

/** `preparePercentAxisChartDownloadTable` 入参。 */
export interface IPreparePercentAxisChartDownloadInput {
  chartType: TChartType;
  data?: Array<Record<string, unknown>>;
  columnName?: IPercentAxisChartDownloadColumn[];
}

type TTableRow = Record<string, unknown>;

/**
 * 判断列名字符串是否为占比虚拟列。
 * 支持 `% 指标`、`%指标`、`t0_% 指标`、`dim_pct_指标` 等命名形态。
 */
function isPercentDownloadColumnName(name: string): boolean {
  const trimmed = name.trim();
  return (
    trimmed.startsWith(PERCENTAGE_PREFIX)
    || trimmed.startsWith('%')
    || EMBEDDED_PERCENT_COLUMN_PATTERN.test(trimmed)
    || trimmed.startsWith('dim_pct_')
  );
}

/**
 * 将占比虚拟列名还原为对应源指标列名。
 * 例：`% t0_revenue` → `t0_revenue`，`t0_% meta_score` → `t0_meta_score`，
 * `t0_% total users` → `t0_total_users`（展示名空格与指标下划线对齐）。
 */
function stripPercentDownloadColumnPrefix(name: string): string {
  const trimmed = name.trim();
  if (trimmed.startsWith(PERCENTAGE_PREFIX)) {
    return normalizeDownloadColumnKey(trimmed.slice(PERCENTAGE_PREFIX.length));
  }
  if (trimmed.startsWith('%')) {
    return normalizeDownloadColumnKey(trimmed.slice(1));
  }
  const embeddedWithSpace = trimmed.match(/^(t\d+)_% (.+)$/);
  if (embeddedWithSpace) {
    return normalizeDownloadColumnKey(
      `${embeddedWithSpace[1]}_${embeddedWithSpace[2]}`,
    );
  }
  const embeddedNoSpace = trimmed.match(/^(t\d+)_%(.+)$/);
  if (embeddedNoSpace) {
    return normalizeDownloadColumnKey(
      `${embeddedNoSpace[1]}_${embeddedNoSpace[2]}`,
    );
  }
  if (trimmed.startsWith('dim_pct_')) {
    return normalizeDownloadColumnKey(trimmed.slice('dim_pct_'.length));
  }
  return normalizeDownloadColumnKey(trimmed);
}

/** 下载列名归一：trim，并把空白压成 `_`，便于 `%` 虚拟列与源指标对齐。 */
function normalizeDownloadColumnKey(name: string): string {
  return name.trim().replace(/\s+/g, '_');
}

/**
 * 根据占比列名在指标列列表中查找对应源指标列名。
 * 支持 `_2` / `_3` 等多配置后缀的宽松匹配；空格与下划线等价。
 */
function findMetricColumnName(
  percentColumnName: string,
  metricColumnNames: string[],
): string | undefined {
  const target = stripPercentDownloadColumnPrefix(percentColumnName);
  const exact = metricColumnNames.find(
    name => normalizeDownloadColumnKey(name) === target,
  );
  if (exact) {
    return exact;
  }
  const withoutSuffix = target.replace(/_\d+$/, '');
  return metricColumnNames.find(
    name => normalizeDownloadColumnKey(name) === withoutSuffix,
  );
}

/** 判断 `column_name` 项是否为占比列（非源指标列）。 */
function isPercentDownloadColumn(col: IPercentAxisChartDownloadColumn): boolean {
  const name = col.name ?? '';
  return isPercentDownloadColumnName(name);
}

/** 判断 `column_name` 项是否为源指标列（`type === 0` 且非占比虚拟列）。 */
function isMetricDownloadColumn(col: IPercentAxisChartDownloadColumn): boolean {
  return Number(col.type) === 0 && !isPercentDownloadColumn(col);
}

/** 判断 `column_name` 项是否为维度列（`type === 1`）。 */
function isDimensionDownloadColumn(col: IPercentAxisChartDownloadColumn): boolean {
  return Number(col.type) === 1;
}

/**
 * 从 `table_data` 单元格提取原始标量值。
 * 兼容直接值与 AliTable 嵌套结构 `{ value, value_type, ... }`。
 */
export function getDownloadRawCellValue(
  raw: unknown,
): string | number | null | undefined {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  if (typeof raw === 'object' && 'value' in raw) {
    const cellValue = (raw as { value: unknown }).value;
    if (cellValue === null || cellValue === undefined) {
      return undefined;
    }
    return cellValue as string | number;
  }
  return raw as string | number;
}

/**
 * 按列名从行对象取值，兼容列名与 key 首尾空格不一致。
 * 依次尝试：精确 key → trim 后 key → 行内 trim 匹配。
 */
export function getDownloadRowCell(
  row: TTableRow,
  columnKey: string,
): unknown {
  if (Object.prototype.hasOwnProperty.call(row, columnKey)) {
    return row[columnKey];
  }
  const trimmedKey = columnKey.trim();
  if (
    trimmedKey !== columnKey
    && Object.prototype.hasOwnProperty.call(row, trimmedKey)
  ) {
    return row[trimmedKey];
  }
  const matchedKey = Object.keys(row).find(key => key.trim() === trimmedKey);
  return matchedKey ? row[matchedKey] : undefined;
}

/**
 * 将单元格原始值转为参与占比计算的数值。
 * 空值、非法值按 0 处理，与 `computeAxisPercentPoints` 一致。
 */
function rawToNumeric(raw: string | number | null | undefined): number {
  if (isNullDataPoint(raw)) {
    return 0;
  }
  const num = Number(raw);
  return Number.isNaN(num) ? 0 : num;
}

/** 将数值占比格式化为下载展示字符串，保留两位小数并追加 `%`。 */
function formatAxisPercentValue(percent: number): string {
  return `${percent.toFixed(2)}%`;
}

/**
 * 为百分比堆叠图 / 百分比折线图补全下载数据中的占比列。
 *
 * 后端 `table_data` 不含 `%` 列 value 时，按 X 轴维度（`column_name` 中首个 `type === 1` 列）
 * 分组，组内用源指标原始值计算 `行指标 / 组内合计 × 100`，写入占比虚拟列。
 * 计算规则与 {@link computeAxisPercentPoints} 一致。
 */
export function enrichPercentAxisChartDownloadData({
  data = [],
  columnName = [],
}: {
  data?: TTableRow[];
  columnName?: IPercentAxisChartDownloadColumn[];
}): TTableRow[] {
  if (!data.length || !columnName.length) {
    return data;
  }

  const dimensionKeys = columnName
    .filter(isDimensionDownloadColumn)
    .map(col => col.name);
  const metricKeys = columnName
    .filter(isMetricDownloadColumn)
    .map(col => col.name);
  const percentColumns = columnName.filter(isPercentDownloadColumn);

  if (!dimensionKeys.length || !metricKeys.length || !percentColumns.length) {
    return data;
  }

  const xAxisKey = dimensionKeys[0];
  const enriched = data.map(row => ({ ...row }));

  percentColumns.forEach(percentCol => {
    const metricKey = findMetricColumnName(percentCol.name, metricKeys);
    if (!metricKey) {
      return;
    }

    const groupRowIndexes = new Map<string, number[]>();
    enriched.forEach((row, rowIndex) => {
      const xValue = String(
        getDownloadRawCellValue(getDownloadRowCell(row, xAxisKey)) ?? '',
      );
      const bucket = groupRowIndexes.get(xValue) ?? [];
      bucket.push(rowIndex);
      groupRowIndexes.set(xValue, bucket);
    });

    groupRowIndexes.forEach(rowIndexes => {
      let total = 0;
      rowIndexes.forEach(rowIndex => {
        const raw = getDownloadRawCellValue(
          getDownloadRowCell(enriched[rowIndex], metricKey),
        );
        total += rawToNumeric(raw);
      });

      rowIndexes.forEach(rowIndex => {
        const raw = getDownloadRawCellValue(
          getDownloadRowCell(enriched[rowIndex], metricKey),
        );
        const numeric = rawToNumeric(raw);
        const percent = total === 0 ? 0 : (numeric / total) * 100;
        enriched[rowIndex][percentCol.name] = formatAxisPercentValue(percent);
      });
    });
  });

  return enriched;
}

/**
 * 下载前统一入口：仅对 `PercentStackBar` / `PercentLine` 执行占比列补算。
 * 其它图表类型原样透传 `data` 与 `columnName`。
 */
export function preparePercentAxisChartDownloadTable({
  chartType,
  data = [],
  columnName = [],
}: IPreparePercentAxisChartDownloadInput): {
  data: TTableRow[];
  columnName: IPercentAxisChartDownloadColumn[];
} {
  const isPercentAxisChart =
    chartType === ChartType.PercentStackBar
    || chartType === ChartType.PercentLine;

  return {
    data: isPercentAxisChart
      ? enrichPercentAxisChartDownloadData({ data, columnName })
      : data,
    columnName,
  };
}
